/**
 * Feuilles de brouillon — côté éditeur (lot 3) : état d'une feuille, porte de
 * validation, génération avec relance, enregistrement.
 *
 * La génération passe par l'edge fn `exercise-v2-scratch-model`, qui N'ÉCRIT
 * RIEN. Chaque modèle proposé passe ici par la porte du moteur
 * (`validateModel`, sans IA) et par la vérification des choix du QCM ; en cas
 * d'échec, on relance l'IA avec le rapport (2 fois au plus). Ce qui reste en
 * échec est enregistré « à revoir » (brouillon non publiable : la base refuse
 * de publier une feuille dont la validation n'est pas positive).
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { isEngineCompatible } from '@jeraisonne/reasoner';
import {
  gateDraft,
  legacyEngineRow,
  modelTrack,
  type GateResult,
  type QuestionQcm,
  type ScratchIssue,
  type SheetRowInput,
} from './scratch-gate';
import { supabase } from '@/lib/supabase';
import {
  runScratchModelPhase,
  V2GenerationError,
  type ScratchModelPhaseResult,
  type ScratchModelRetry,
} from '@/lib/exercise-v2/generation-client';

export type ScratchStatus = 'draft' | 'published' | 'disabled' | 'ineligible';

/** Une ligne `scratch_models`, telle que l'éditeur la lit (RLS auteur / admin). */
export interface ScratchRow {
  id: string;
  question_block_id: string;
  status: ScratchStatus;
  ineligible_reason: string | null;
  validation: Record<string, unknown> | null;
  max_hora: number;
  model: unknown;
  engine_version: number;
  /** version de la feuille (migration 0532) : ancien moteur ou noyau */
  /** `packs` : version du moteur v2 (lot V3), tirée de celle du noyau */
  track: 'legacy' | 'kernel' | 'packs';
  generated_by: 'ai' | 'manual';
  updated_at: string;
}

/** Ce que l'éditeur affiche pour une question. */
export type SheetState = 'none' | 'ready' | 'review' | 'published' | 'disabled' | 'ineligible';

const db = () => supabase as unknown as SupabaseClient;

/** Validation positive enregistrée (la seule publiable). */
export function isValidated(row: Pick<ScratchRow, 'validation'> | null | undefined): boolean {
  return row?.validation?.ok === true;
}

export function sheetState(row: ScratchRow | null | undefined): SheetState {
  if (!row) return 'none';
  if (row.status === 'ineligible') return 'ineligible';
  if (row.status === 'published') return 'published';
  if (row.status === 'disabled') return 'disabled';
  return isValidated(row) ? 'ready' : 'review';
}

export const SHEET_STATE_LABEL: Record<SheetState, string> = {
  none: 'Aucune feuille',
  ready: 'Brouillon prêt',
  review: 'À revoir',
  published: 'Publiée',
  disabled: 'Désactivée',
  ineligible: 'Hors champ',
};

/** Problèmes relevés par la porte (« à revoir »). */
export function rowIssues(row: ScratchRow | null | undefined): ScratchIssue[] {
  const raw = row?.validation?.issues;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((i) => {
    const o = i as Record<string, unknown>;
    if (typeof o?.message !== 'string') return [];
    return [
      {
        code: typeof o.code === 'string' ? o.code : 'issue',
        message: o.message,
        ...(typeof o.line === 'string' ? { line: o.line } : {}),
      },
    ];
  });
}

/** Corrigé trouvé par le moteur (lignes du solveur). */
export function rowSolution(row: ScratchRow | null | undefined): { text: string; label: string }[] {
  const raw = row?.validation?.solution;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((s) => {
    const o = s as Record<string, unknown>;
    return typeof o?.text === 'string'
      ? [{ text: o.text, label: typeof o.label === 'string' ? o.label : '' }]
      : [];
  });
}

export {
  checkChoices,
  gateDraft,
  LEGACY_ENGINE_REASON,
  legacyEngineRow,
  modelTrack,
  orderedQuestionBlocks,
  questionQcm,
  sheetBlocker,
  type GateResult,
  type QuestionQcm,
  type ScratchIssue,
  type SheetRowInput,
  type SheetTrack,
} from './scratch-gate';

/** Le rapport tel que l'edge fn l'accepte en relance (bornes du schéma serveur). */
function retryIssues(issues: ScratchIssue[]): ScratchModelRetry['issues'] {
  return issues.slice(0, 30).map((i) => ({
    code: i.code.slice(0, 40),
    message: i.message.slice(0, 400),
    ...(i.line ? { line: i.line.slice(0, 400) } : {}),
  }));
}

export type GenerateOutcome =
  | { kind: 'ready' | 'review' | 'ineligible'; row: SheetRowInput; attempts: number }
  | { kind: 'not_linked'; attempts: number };

export interface GenerateOptions {
  exerciseId: string;
  questionIndex: number;
  /** id du bloc question attendu à cet index : garde-fou si l'exercice a bougé entre-temps */
  questionId: string;
  qcm: QuestionQcm;
  customInstructions?: string;
  /** admin : tous les essais en Opus (« Régénérer avec Opus ») */
  forceOpus?: boolean;
  /** relances avec le rapport de la porte (2 par défaut) */
  maxRetries?: number;
  /** pour les tests */
  run?: typeof runScratchModelPhase;
  /** appelé avant chaque appel à l'IA (1, 2, 3…) */
  onAttempt?: (attempt: number) => void;
}

/**
 * Escalade du modèle : seulement à la DERNIÈRE relance, quand au moins deux
 * essais au modèle par défaut ont déjà échoué (le 1er essai n'escalade jamais).
 */
export function shouldEscalate(attempt: number, maxRetries: number): boolean {
  return maxRetries >= 2 && attempt === maxRetries + 1;
}

/** Génère la feuille d'UNE question, la passe à la porte et relance au besoin. N'enregistre rien. */
export async function generateQuestionSheet(opts: GenerateOptions): Promise<GenerateOutcome> {
  const run = opts.run ?? runScratchModelPhase;
  const maxRetries = opts.maxRetries ?? 2;
  let retry: ScratchModelRetry[] | undefined;
  let last: GateResult | null = null;
  for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
    opts.onAttempt?.(attempt);
    const res: ScratchModelPhaseResult = await run(opts.exerciseId, {
      questionIndexes: [opts.questionIndex],
      retry,
      customInstructions: opts.customInstructions,
      // Dernière relance après des échecs : modèle plus fort (Opus, côté serveur).
      escalate: shouldEscalate(attempt, maxRetries),
      ...(opts.forceOpus ? { forceOpus: true } : {}),
    });
    if (res.skipped?.some((s) => s.question_index === opts.questionIndex))
      return { kind: 'not_linked', attempts: attempt };
    const draft = res.drafts?.find((d) => d.question_index === opts.questionIndex);
    if (!draft) throw new V2GenerationError('Aucune proposition de l’IA pour cette question.');
    if (draft.question_block_id !== opts.questionId)
      throw new V2GenerationError(
        'L’exercice a changé pendant la génération (questions déplacées ?) : recharge la page puis relance.',
        'question_mismatch',
      );
    if (!draft.eligible) {
      return {
        kind: 'ineligible',
        attempts: attempt,
        row: {
          model: {},
          status: 'ineligible',
          ineligible_reason: draft.reason.trim().slice(0, 500) || 'Hors du champ du moteur.',
          validation: { ok: false, category: draft.category },
          max_hora: 0,
          engine_version: res.engine_version,
        },
      };
    }
    // nouvelles feuilles : nouveau moteur seulement (pas de relance pour l'ancien)
    if (modelTrack(draft.model) === 'legacy')
      return { kind: 'ineligible', attempts: attempt, row: legacyEngineRow(res.engine_version) };
    last = gateDraft(draft.model, opts.qcm, draft.notes);
    if (last.ok) return { kind: 'ready', row: last.row, attempts: attempt };
    retry = [
      {
        question_index: opts.questionIndex,
        previous_model: draft.model,
        issues: retryIssues(last.issues),
      },
    ];
  }
  return { kind: 'review', row: last!.row, attempts: maxRetries + 1 };
}

const errorCode = (e: unknown) => (e instanceof V2GenerationError ? e.code : undefined);
const errorText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** Fonction absente ou injoignable (pas encore déployée, réseau coupé, CORS). */
function isUnreachable(e: unknown): boolean {
  return /function.*not found|not.?found.*function|404|failed to send a request|failed to fetch|fetch failed/i.test(
    errorText(e),
  );
}

/** Erreur qui vaudra pour toutes les questions : inutile de continuer un lot. */
export function isFatalGenerationError(e: unknown): boolean {
  const code = errorCode(e);
  return (
    isUnreachable(e) ||
    /budget/i.test(errorText(e)) ||
    [
      'no_skillcard_link',
      'parametric_exercise_not_supported',
      'not_author',
      'forbidden',
      'unauthorized',
      'question_mismatch',
      'daily_budget_exceeded',
    ].includes(code ?? '')
  );
}

/** Message lisible pour une erreur de génération. */
export function generationErrorMessage(e: unknown): string {
  const code = errorCode(e);
  const message = errorText(e);
  if (code === 'no_skillcard_link')
    return 'Cet exercice n’est relié à aucune SKILLCARD publiée : pas de feuille de brouillon pour l’instant.';
  if (code === 'parametric_exercise_not_supported')
    return 'Exercice paramétré : les feuilles de brouillon ne sont pas encore possibles.';
  if (code === 'not_author' || code === 'forbidden')
    return 'Seul l’auteur de l’exercice (ou un admin) peut générer ses feuilles.';
  if (code === 'daily_budget_exceeded' || /budget/i.test(message))
    return 'Budget IA du jour atteint : réessaie demain.';
  if (isUnreachable(e))
    return 'La génération n’est pas joignable (fonction exercise-v2-scratch-model pas encore déployée ?).';
  return message || 'Erreur inattendue.';
}

/** Une feuille que le player peut charger (version du moteur compatible). */
export function isEngineReady(row: Pick<ScratchRow, 'engine_version'>): boolean {
  return isEngineCompatible(row.engine_version);
}

// ── lecture / écriture `scratch_models` (RLS auteur / admin) ───────────────

export async function fetchScratchRows(exerciseId: string): Promise<ScratchRow[]> {
  const { data, error } = await db()
    .from('scratch_models')
    .select(
      'id, question_block_id, status, ineligible_reason, validation, max_hora, model, engine_version, track, generated_by, updated_at',
    )
    .eq('exercise_id', exerciseId);
  if (error) throw new Error(error.message);
  return (data ?? []) as ScratchRow[];
}

/**
 * Enregistre (ou remplace) la feuille NOUVEAU MOTEUR d'une question. Une
 * feuille publiée redevient brouillon. La feuille de l'ancien moteur, s'il y en
 * a une, n'est jamais touchée (les apps pas encore à jour la jouent).
 */
export async function saveSheet(
  exerciseId: string,
  questionId: string,
  row: SheetRowInput,
): Promise<void> {
  const { error } = await db().from('scratch_models').upsert(
    {
      exercise_id: exerciseId,
      question_block_id: questionId,
      engine_version: row.engine_version,
      model: row.model,
      status: row.status,
      ineligible_reason: row.ineligible_reason,
      validation: row.validation,
      max_hora: row.max_hora,
      generated_by: 'ai',
      track: 'kernel',
    },
    { onConflict: 'question_block_id,track' },
  );
  if (error) throw new Error(error.message);
}

/** Publier, désactiver, ou remettre en brouillon. La base refuse de publier une feuille non validée. */
export async function setSheetStatus(
  ids: string[],
  status: 'published' | 'disabled' | 'draft',
): Promise<void> {
  if (!ids.length) return;
  const { data, error } = await db()
    .from('scratch_models')
    .update({ status })
    .in('id', ids)
    .select('id');
  if (error) throw new Error(error.message);
  const updated = (data ?? []).length;
  if (updated < ids.length)
    throw new Error(
      updated
        ? `${updated} feuille(s) sur ${ids.length} mises à jour : les autres ne te sont pas accessibles.`
        : 'Aucune feuille mise à jour : seul l’auteur de l’exercice (ou un admin) peut le faire.',
    );
}
